/**
 * Popup-side wiring for the MF device-settings bridge (SM-1187 follow-up).
 *
 * Kept as a plain function (opener + parentOrigin injected) rather than living
 * inside the React hook, so the postMessage/pagehide behavior is unit-testable
 * without rendering. See `useMeetingPopupDeviceBridge` for the hook wrapper and
 * `protocol.ts` for the wire contract.
 */

import { getHostDeviceLabels } from './deviceBridge';
import {
  isMeetingWindowMessage,
  MEETING_WINDOW_MESSAGE_SOURCE,
  type MeetingWindowDeviceLabels,
} from './protocol';
import { applySettingsPayloadToPopup } from './settingsBridge';

const hasAnyLabel = (
  labels: MeetingWindowDeviceLabels | null,
): labels is MeetingWindowDeviceLabels =>
  !!(labels && (labels.cameraLabel || labels.microphoneLabel || labels.audioOutputLabel));

/**
 * Wire the popup's settings bridge to its opener:
 * - Forward: apply the host's Meet Settings (pushed by the opener after the auth
 *   handshake) into this popup's storage, so `buildJitsiConfigOverwrite` sees
 *   the user's real prejoin / start-muted / device choices instead of defaults.
 *   `onSettingsApplied` fires once that has landed; the caller uses it to
 *   release the gate that holds meeting initialization.
 * - Reverse: push the popup's current device selection back to the opener as
 *   soon as an in-meeting change is detected, and again on `pagehide` as a final
 *   flush. Pushing on change (rather than only on `pagehide`) is deliberate:
 *   when the meeting is closed via hangup the popup's React tree often unmounts
 *   before `window.close()`, which would tear down the `pagehide` listener
 *   before it ever fired.
 *
 * Returns a cleanup function that removes all listeners.
 */
export const createMeetingPopupDeviceBridge = (
  opener: Window,
  parentOrigin: string,
  onSettingsApplied?: () => void,
): (() => void) => {
  let latestLabels: MeetingWindowDeviceLabels | null = null;

  const postUpdate = (labels: MeetingWindowDeviceLabels | null): void => {
    if (!hasAnyLabel(labels)) {
      return;
    }
    try {
      opener.postMessage(
        {
          source: MEETING_WINDOW_MESSAGE_SOURCE,
          type: 'device-update',
          devices: labels,
        },
        parentOrigin,
      );
    } catch {
      // opener may already be gone; nothing else we can do
    }
  };

  // Resolve the popup's current selection into labels, cache it, and optionally
  // push it to the opener immediately.
  const refreshLatest = (push: boolean): void => {
    void getHostDeviceLabels().then((labels) => {
      latestLabels = labels;
      if (push) {
        postUpdate(labels);
      }
    });
  };

  const onMessage = (event: MessageEvent): void => {
    if (event.origin !== parentOrigin || event.source !== opener) {
      return;
    }
    if (!isMeetingWindowMessage(event.data) || event.data.type !== 'settings') {
      return;
    }
    void applySettingsPayloadToPopup(event.data.settings)
      .catch((err) => {
        console.warn('[SettingsBridge/popup] failed to apply host settings', err);
      })
      .finally(() => {
        // Seed the reverse cache from what we just applied, then let the meeting
        // start — even on failure, so a bad payload can't strand the popup.
        refreshLatest(false);
        onSettingsApplied?.();
      });
  };

  const onDeviceChange = (): void => {
    refreshLatest(true);
  };

  const onPageHide = (): void => {
    postUpdate(latestLabels);
  };

  window.addEventListener('message', onMessage);
  window.addEventListener('settings-devices-changed', onDeviceChange);
  window.addEventListener('pagehide', onPageHide);

  // Seed the cache from whatever is already persisted in this popup.
  refreshLatest(false);

  return () => {
    window.removeEventListener('message', onMessage);
    window.removeEventListener('settings-devices-changed', onDeviceChange);
    window.removeEventListener('pagehide', onPageHide);
  };
};
